import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Menu, Image } from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import util from 'utils/utils';

import IdeaVotedContent from './IdeaVoted';
import StageChangedContent from './StageChanged';
import UnlockedAchievementContent from './UnlockedAchievement';
import MentionedContent from './Mentioned';
import IdeaSubmittedContent from './IdeaSubmitted';
import IdeaCommentedContent from './IdeaCommented';
import DiscussionPostedContent from './DiscussionPosted';
import ClosingSoonContent from './ClosingSoon';
import AddedAsCollaboratorContent from './AddedAsCollaborator';
import ProjectIdeaCommented from './Project';
import AssignedAssessorContent from './AssignedAssessor';
import GroupInvitedContent from './Group/Invited';
import GroupLeftContent from './Group/Left';
import GroupJoinRequestedContent from './Group/JoinRequested';

import JoinRequestedActions from './Group/JoinRequestedActions';
import InvitedActions from './Group/InvitedActions';

const mentions = [
  {
    type: 'mentionedGroupDiscussion',
    message: 'mentioned you in a group discussion',
    link: 'groups',
    location: 'discussion',
    id: 'group',
  },
  {
    type: 'mentionedIdeaComment',
    message: 'mentioned you in an idea comment',
    link: 'ideas',
    location: '',
    id: 'idea',
  },
  {
    type: 'mentionedIdeaField',
    message: 'mentioned you in an idea text',
    link: 'ideas',
    location: '',
    id: 'idea',
  },
  {
    type: 'mentionedIdeaProjectComment',
    message: 'mentioned you in a project board comment',
    link: 'challenges',
    location: 'board',
  },
];

const notificationAllowlist = [
  'ideaCommented',
  'ideaSubmitted',
  'ideaVoted',
  'collaboratorAdded',
  'mentionedGroupDiscussion',
  'mentionedIdeaComment',
  'mentionedIdeaField',
  'mentionedIdeaProjectComment',
  'collaboratorRemoved',
  'closingSoon',
  'stageChanged',
  'discussionPosted',
  'unlockedAchievement',
  'projectIdeaCommented',
  'assignedAssessor',
  'groupLeft',
  'groupJoinRequested',
  'groupInvited',
];

const actionableNotifications = [
  'groupJoinRequested',
  'groupInvited',
];

export const NotificationLocation = (notification) => {
  switch (notification.notificationType) {
    case 'ideaCommented':
      return `/ideas/${notification.idea}?comment=${notification.events[0]?.commentId}#comments`;
    case 'ideaVoted':
    case 'collaboratorAdded':
    case 'collaboratorRemoved':
      return `/ideas/${notification.idea}`;
    case 'stageChanged':
    case 'closingSoon':
      return `/challenges/${notification.challenge}/`;
    case 'assignedAssessor':
      return `/challenges/${notification.challenge}/assessments`;
    case 'ideaSubmitted': {
      const { events = [] } = notification;
      if (events.length > 1) {
        return `/challenges/${notification.challenge}/ideas`;
      }
      return `/ideas/${events[0].idea}`;
    }
    case 'discussionPosted': return `/groups/${notification.group}/discussion`;
    case 'mentionedGroupDiscussion':
    case 'mentionedIdeaComment':
    case 'mentionedIdeaField': {
      const mentionedInfo = mentions.find((m) => m.type === notification.notificationType);
      return `/${mentionedInfo.link}/${notification[mentionedInfo.id]}/${mentionedInfo.location}${notification.queryString ? `?${notification.queryString}` : ''}`;
    }
    case 'mentionedIdeaProjectComment':
      return `/challenges/${notification.challenge}/board?idea=${notification.events[0].idea}`;
    case 'unlockedAchievement': return '/achievements';
    case 'projectIdeaCommented': return `/challenges/${notification.challenge}/board?idea=${notification.idea}`;
    case 'groupLeft':
      return `/groups/${notification.group}`;
    case 'groupJoinRequested':
    case 'groupInvited':
      return '/';
    default: return null;
  }
};

const AvatarImage = (source) => <Image style={{
  width: 50, height: 50, objectFit: 'cover', borderRadius: 10,
}} src={source} />;

export const NotificationAvatarImage = (notification, user) => {
  switch (notification.notificationType) {
    case 'ideaCommented':
    case 'ideaVoted':
    case 'collaboratorAdded':
    case 'mentionedGroupDiscussion':
    case 'mentionedIdeaComment':
    case 'collaboratorRemoved':
    case 'projectIdeaCommented':
    case 'mentionedIdeaProjectComment':
      return AvatarImage(util.avatarUrl(notification?.events[0]?.ownerUser || {}));
    case 'mentionedIdeaField':
      return AvatarImage(util.avatarUrl(notification?.events[0]?.user || {}));
    case 'closingSoon':
    case 'stageChanged':
    case 'ideaSubmitted':
    case 'assignedAssessor':
      return AvatarImage(util.challengeImage(notification.ownerChallenge));
    case 'discussionPosted':
    case 'groupLeft':
    case 'groupJoinRequested':
    case 'groupInvited':
      return AvatarImage(util.groupCoverImage(notification.ownerGroup));
    case 'unlockedAchievement':
      return AvatarImage(util.avatarUrl(user));
    default: return null;
  }
};

export const NotificationContent = (notification) => {
  switch (notification.notificationType) {
    case 'ideaCommented': return <IdeaCommentedContent events={notification.events} ownerIdea={notification.ownerIdea} />;
    case 'closingSoon': return <ClosingSoonContent ownerChallenge={notification.ownerChallenge} />;
    case 'stageChanged': return <StageChangedContent ownerChallenge={notification.ownerChallenge} />;
    case 'discussionPosted': return <DiscussionPostedContent ownerGroup={notification.ownerGroup} events={notification.events} />;
    case 'ideaSubmitted': return <IdeaSubmittedContent events={notification.events} ownerChallenge={notification.ownerChallenge} />;
    case 'ideaVoted': return <IdeaVotedContent events={notification.events} ownerIdea={notification.ownerIdea} />;
    case 'collaboratorAdded': return <AddedAsCollaboratorContent events={notification.events} ownerIdea={notification.ownerIdea} />;
    case 'collaboratorRemoved': return <AddedAsCollaboratorContent events={notification.events} ownerIdea={notification.ownerIdea} removed />;
    case 'mentionedGroupDiscussion':
    case 'mentionedIdeaComment':
    case 'mentionedIdeaField':
    case 'mentionedIdeaProjectComment':
      return <MentionedContent notification={notification} mentions={mentions} events={notification.events} notificationType={notification.notificationType} />;
    case 'unlockedAchievement':
      return <UnlockedAchievementContent events={notification.events} />;
    case 'projectIdeaCommented':
      return <ProjectIdeaCommented events={notification.events} ownerIdea={notification.ownerIdea} ownerChallenge={notification.ownerChallenge} />;
    case 'assignedAssessor':
      return <AssignedAssessorContent events={notification.events} ownerChallenge={notification.ownerChallenge} />;
    case 'groupLeft':
      return <GroupLeftContent events={notification.events} ownerGroup={notification.ownerGroup} />;
    case 'groupJoinRequested':
      return <GroupJoinRequestedContent events={notification.events} ownerGroup={notification.ownerGroup} />;
    case 'groupInvited':
      return <GroupInvitedContent events={notification.events}ownerGroup={notification.ownerGroup} />;
    default: return null;
  }
};

export const NotificationActions = ({ notification, ...rest }) => {
  switch (notification.notificationType) {
    case 'groupJoinRequested': return <JoinRequestedActions events={notification.events} {...rest} />;
    case 'groupInvited': return <InvitedActions events={notification.events} {...rest} />;
    default: return null;
  }
};

function NotificationItem({
  notification, user, markNotificationAsInteracted, invitations, onInvitationHandled,
}) {
  const { notificationType, ownerGroup, interacted } = notification;

  if (!notificationType) return null;
  if (!(notificationAllowlist.indexOf(notificationType) > -1)) { return null; }
  if ((notificationType === 'discussedPosted') && !ownerGroup) { return null; }

  if (notification.ownerChallenge && notification.ownerChallenge === null) notification.ownerChallenge = { name: 'Undefined' };
  if (notification.ownerGroup && notification.ownerGroup === null) notification.ownerGroup = { name: 'Undefined' };
  if (notification.ownerIdea && notification.ownerIdea === null) notification.ownerIdea = { name: 'Undefined' };

  return (
    <Menu.Item
      fitted
      link
      as={Link}
      to={NotificationLocation(notification)}
      style={{
        borderBottom: '1px solid', borderColor: '#E9EBEE', padding: '.833em 1em', borderRadius: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', textOverflow: 'ellipsis', backgroundColor: interacted ? null : '#e9ebee', marginTop: 5, marginBottom: 5, width: '99%',
      }}
      onClick={() => markNotificationAsInteracted(notification._id)}
    >
      <div>
        {NotificationAvatarImage(notification, user)}
      </div>
      <div style={{ marginLeft: 10, flex: 1 }}>
        <p style={{
          textAlign: 'left', marginBottom: 0, lineHeight: '15px', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {NotificationContent(notification)}
        </p>
        <p style={{ color: 'gray', marginTop: 3 }}>
          {moment(notification.createdAt).fromNow()}
        </p>
      </div>
      {actionableNotifications.indexOf(notificationType) > -1
        ? (
          <div>
            <NotificationActions invitations={invitations} notification={notification} onAccept={onInvitationHandled} onReject={onInvitationHandled} />
          </div>
        ) : null
      }
    </Menu.Item>
  );
}

export default withTranslation()(withRouter(NotificationItem));
